import { Head, router, useForm } from "@inertiajs/react"
import { useState } from "react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Membership {
  org_id: number
  org_name: string
  org_slug: string
  role: string
  accounting_denied: boolean
}
interface UserRow {
  id: number
  name: string
  email: string
  role: string
  created_at: string
  memberships: Membership[]
}
interface OrgOption {
  id: number
  name: string
  slug: string
}
interface Props {
  users: UserRow[]
  query: string
  truncated: boolean
  org_options: OrgOption[]
  app_roles: string[]
  org_roles: string[]
  current_user_id: number
  generated_password: { email: string; password: string } | null
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Admin Console", href: "/admin/console" },
  { title: "Users", href: "/admin/users" },
]

function fmtDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export default function AdminUsers(props: Props) {
  const {
    users,
    org_options,
    app_roles,
    org_roles,
    current_user_id,
    generated_password,
  } = props
  const [q, setQ] = useState(props.query)
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  // Derive the open user from the live prop set so inline edits (role,
  // accounting, membership) reflect in the panel after a partial reload.
  const selected = users.find((u) => u.id === selectedId) ?? null

  const form = useForm({
    name: "",
    email: "",
    app_role: "member",
    org_id: "",
    org_role: "viewer",
  })

  function search(e: React.FormEvent) {
    e.preventDefault()
    router.get(
      "/admin/users",
      { q },
      { preserveState: true, preserveScroll: true, replace: true },
    )
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    form.post("/admin/users", {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        setCreateOpen(false)
      },
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Users · Admin" />
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Users</h1>
            <p className="text-muted-foreground text-sm">
              Create users, manage org roles, and set feature access.
            </p>
          </div>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button>New user</Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={submit}>
                <DialogHeader>
                  <DialogTitle>New user</DialogTitle>
                  <DialogDescription>
                    Creates the account with a generated password (shown once).
                    Optionally add them to an organization.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="u-email">Email</Label>
                    <Input
                      id="u-email"
                      type="email"
                      value={form.data.email}
                      onChange={(e) => form.setData("email", e.target.value)}
                      placeholder="person@company.com"
                      autoFocus
                    />
                    {form.errors.email && (
                      <p className="text-destructive text-sm">
                        {form.errors.email}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="u-name">Name</Label>
                    <Input
                      id="u-name"
                      value={form.data.name}
                      onChange={(e) => form.setData("name", e.target.value)}
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>App role</Label>
                    <Select
                      value={form.data.app_role}
                      onValueChange={(v) => form.setData("app_role", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {app_roles.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      superadmin / admin bypass all feature-access restrictions.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Add to org (optional)</Label>
                      <Select
                        value={form.data.org_id}
                        onValueChange={(v) => form.setData("org_id", v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent>
                          {org_options.map((o) => (
                            <SelectItem key={o.id} value={String(o.id)}>
                              {o.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Org role</Label>
                      <Select
                        value={form.data.org_role}
                        onValueChange={(v) => form.setData("org_role", v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {org_roles.map((r) => (
                            <SelectItem key={r} value={r}>
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={form.processing || !form.data.email.trim()}
                  >
                    {form.processing ? "Creating…" : "Create user"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {generated_password && (
          <Alert>
            <AlertTitle>Password for {generated_password.email}</AlertTitle>
            <AlertDescription>
              <span className="font-mono">{generated_password.password}</span>
              <span className="text-muted-foreground ml-2 text-xs">
                Copy it now — it isn&apos;t shown again.
              </span>
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={search} className="flex gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email or name…"
            className="max-w-sm"
          />
          <Button type="submit" variant="outline">
            Search
          </Button>
        </form>

        {props.truncated && (
          <p className="text-muted-foreground text-sm">
            Showing the first 200 matches — narrow your search to see more.
          </p>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead className="w-28">App role</TableHead>
                <TableHead>Organizations</TableHead>
                <TableHead className="w-28">Joined</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const restricted = u.memberships.some(
                  (m) => m.accounting_denied,
                )
                return (
                  <TableRow
                    key={u.id}
                    className="cursor-pointer"
                    onClick={() => setSelectedId(u.id)}
                  >
                    <TableCell>
                      <div className="font-medium">{u.email}</div>
                      {u.name && u.name !== u.email && (
                        <div className="text-muted-foreground text-xs">
                          {u.name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.role === "member" ? (
                        <span className="text-muted-foreground text-sm">
                          member
                        </span>
                      ) : (
                        <Badge variant="outline">{u.role}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.memberships.length === 0 ? (
                        <span className="text-muted-foreground text-sm">—</span>
                      ) : (
                        <span className="flex items-center gap-2 text-sm">
                          <span>
                            {u.memberships.length} org
                            {u.memberships.length === 1 ? "" : "s"}
                          </span>
                          {restricted && (
                            <Badge
                              variant="outline"
                              className="text-muted-foreground text-xs"
                            >
                              accounting restricted
                            </Badge>
                          )}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {fmtDate(u.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedId(u.id)
                        }}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
              {users.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    No users match.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <Sheet
          open={selected !== null}
          onOpenChange={(o) => !o && setSelectedId(null)}
        >
          <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
            {selected && (
              <UserPanel
                key={selected.id}
                user={selected}
                orgOptions={org_options}
                appRoles={app_roles}
                orgRoles={org_roles}
                isSelf={selected.id === current_user_id}
              />
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  )
}

function UserPanel({
  user,
  orgOptions,
  appRoles,
  orgRoles,
  isSelf,
}: {
  user: UserRow
  orgOptions: OrgOption[]
  appRoles: string[]
  orgRoles: string[]
  isSelf: boolean
}) {
  const memberOrgIds = new Set(user.memberships.map((m) => m.org_id))
  const addable = orgOptions.filter((o) => !memberOrgIds.has(o.id))

  const [name, setName] = useState(user.name)
  const [role, setRole] = useState(user.role)
  const [saving, setSaving] = useState(false)
  const dirty = name !== user.name || role !== user.role

  const [addOrg, setAddOrg] = useState("")
  const [addRole, setAddRole] = useState("viewer")

  // Inline edits keep the panel open (preserveState) and refresh the prop set.
  const live = { preserveScroll: true, preserveState: true }

  function saveProfile() {
    router.patch(
      `/admin/users/${user.id}`,
      { name, app_role: role },
      {
        ...live,
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      },
    )
  }
  function setMembershipRole(orgId: number, r: string) {
    router.post(
      `/admin/users/${user.id}/membership`,
      { org_id: orgId, role: r },
      live,
    )
  }
  function remove(orgId: number, orgName: string) {
    if (!window.confirm(`Remove ${user.email} from ${orgName}?`)) return
    setMembershipRole(orgId, "remove")
  }
  function setAccounting(orgId: number, allowed: boolean) {
    router.post(
      `/admin/users/${user.id}/feature_access`,
      {
        org_id: orgId,
        feature: "accounting",
        effect: allowed ? "default" : "deny",
      },
      live,
    )
  }
  function addMembership() {
    if (!addOrg) return
    router.post(
      `/admin/users/${user.id}/membership`,
      { org_id: addOrg, role: addRole },
      { ...live, onSuccess: () => setAddOrg("") },
    )
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle className="truncate">{user.email}</SheetTitle>
        <SheetDescription>
          Manage this user&apos;s profile, org memberships, and feature access.
        </SheetDescription>
      </SheetHeader>

      <div className="space-y-6 px-4 pb-6">
        {/* Profile */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Profile</h3>
          <div className="space-y-1.5">
            <Label htmlFor="p-name">Name</Label>
            <Input
              id="p-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>App role</Label>
            <Select value={role} onValueChange={setRole} disabled={isSelf}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {appRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              {isSelf
                ? "You can't change your own app role."
                : "superadmin / admin bypass all feature-access restrictions."}
            </p>
          </div>
          <Button
            size="sm"
            onClick={saveProfile}
            disabled={!dirty || saving || !name.trim()}
          >
            {saving ? "Saving…" : "Save profile"}
          </Button>
        </section>

        {/* Memberships */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Organizations</h3>
          {user.memberships.length === 0 && (
            <p className="text-muted-foreground text-sm">No org memberships.</p>
          )}
          {user.memberships.map((m) => (
            <div key={m.org_id} className="space-y-2 rounded-md border p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {m.org_name}
                  </div>
                  <div className="text-muted-foreground font-mono text-xs">
                    /{m.org_slug}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive h-7"
                  onClick={() => remove(m.org_id, m.org_name)}
                >
                  Remove
                </Button>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground text-xs">Role</span>
                  <Select
                    value={m.role}
                    onValueChange={(v) => setMembershipRole(m.org_id, v)}
                  >
                    <SelectTrigger className="h-8 w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {orgRoles.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-1.5">
                  <span className="text-muted-foreground text-xs">
                    Accounting
                  </span>
                  <Switch
                    checked={!m.accounting_denied}
                    onCheckedChange={(c) => setAccounting(m.org_id, c)}
                  />
                </label>
              </div>
            </div>
          ))}

          {addable.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t pt-3">
              <span className="text-muted-foreground text-xs">Add to org</span>
              <Select value={addOrg} onValueChange={setAddOrg}>
                <SelectTrigger className="h-8 w-44">
                  <SelectValue placeholder="Choose org…" />
                </SelectTrigger>
                <SelectContent>
                  {addable.map((o) => (
                    <SelectItem key={o.id} value={String(o.id)}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={addRole} onValueChange={setAddRole}>
                <SelectTrigger className="h-8 w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {orgRoles.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={addMembership}
                disabled={!addOrg}
              >
                Add
              </Button>
            </div>
          )}
        </section>
      </div>
    </>
  )
}
